import { jwtDecode } from "jwt-decode";

const RETURN_URL_KEY = "sf_return_url";

/**
 * Saves the current URL (pathname + search + hash) so the user can be
 * redirected back after re-authentication.
 *
 * Only meaningful paths are persisted – the login / signup / autologin
 * pages are excluded so that the user doesn't loop back to them.
 */
export const saveReturnUrl = (): void => {
	const { pathname, search, hash } = window.location;
	const ignoredPaths = ["/login", "/signup", "/autologin"];

	if (ignoredPaths.some((p) => pathname.startsWith(p))) return;

	const returnUrl = `${pathname}${search}${hash}`;
	sessionStorage.setItem(RETURN_URL_KEY, returnUrl);
};

/**
 * Retrieves and clears the previously saved return URL.
 * Returns `null` when nothing was stored.
 */
export const consumeReturnUrl = (): string | null => {
	const url = sessionStorage.getItem(RETURN_URL_KEY);
	sessionStorage.removeItem(RETURN_URL_KEY);
	return url;
};

/**
 * Checks whether a JWT token is expired (or will expire within the
 * given `bufferSeconds` window). Returns `true` when the token should
 * be considered invalid.
 */
export const isTokenExpired = (token: string, bufferSeconds = 60): boolean => {
	if (!token) return true;

	try {
		const decoded = jwtDecode<{ exp?: number }>(token);
		if (!decoded.exp) return true;

		const nowInSeconds = Math.floor(Date.now() / 1000);
		return decoded.exp - bufferSeconds <= nowInSeconds;
	} catch {
		return true;
	}
};
